import React, { useEffect, useState } from 'react';
import { CustomTitle, ToggleSwitch } from '../../components';
import { Button, Flex, message, Typography } from 'antd';
import {
  AiFlowToggle,
  GetAiSettings,
  UpdateAiSettings,
} from '../../apis/settings';
import { useToken } from '../../common/helper';
import { useSpin } from '../../components/SpinContext';
import { useUnAuthorize } from '../../hooks';
import PhoneInput from 'react-phone-input-2';
import { isValidPhoneNumber } from 'libphonenumber-js';
import 'react-phone-input-2/lib/style.css';

const { Text, Paragraph } = Typography;

interface AIworkflowProps {
  title?: string;
}

/**
 * AI workflow settings component that allows users to toggle AI functionality
 *
 * @param title - Optional title for the settings section
 */
const AIworkflow: React.FC<AIworkflowProps> = ({ title }) => {
  const { token, decodedUserToken } = useToken();
  const decodedToken = decodedUserToken();
  const { unauthorize } = useUnAuthorize();
  const [toggle, setToggle] = useState<boolean | null>(null);
  const [transferNumber, setTransferNumber] = useState('');
  const [initialTransferNumber, setInitialTransferNumber] = useState('');
  const [transferNumberError, setTransferNumberError] = useState('');
  const [isTransferNumberChanged, setIsTransferNumberChanged] = useState(false);
  const [agentNumber, setAgentNumber] = useState('');
  const { showSpin, hideSpin } = useSpin();
  const refreshToken = localStorage.getItem('@refreshToken');

  useEffect(() => {
    showSpin();
    GetAiSettings(token)
      .then((res) => {
        const data = res?.data?.data?.data;
        if (data) {
          if (data.ai_enabl !== undefined) {
            setToggle(data.ai_enabl);
          }

          if (data.ai_transfer_number !== undefined) {
            const number = (data.ai_transfer_number || '').replace(/^\+/, '');
            setTransferNumber(number);
            setInitialTransferNumber(number);
          }

          if (data.ai_agent_number !== undefined) {
            const number = (data.ai_agent_number || '').replace(/^\+/, '');
            setAgentNumber(number);
          }
        }
      })
      .catch((error) => {
        unauthorize(error);
      })
      .finally(() => {
        hideSpin();
      });
  }, [hideSpin, showSpin, token, unauthorize]);
  const toggleSwitch = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!decodedToken?.is_company_owner) {
      message.error('You are not a company owner');
      return;
    }
    const checked = e.target.checked;
    setToggle(checked);
    showSpin();

    const model = {
      ai_enabl: checked ? 'true' : 'false',
      refresh: refreshToken,
    };

    AiFlowToggle(token, model)
      .then((res) => {
        const responseData = res?.data?.data;
        if (responseData?.is_success && responseData?.data) {
          if (responseData?.data?.access) {
            localStorage.setItem('@token', responseData.data.access);
          }
          if (responseData?.data?.refresh) {
            localStorage.setItem('@refreshToken', responseData.data.refresh);
          }
        }
      })
      .catch((error) => {
        unauthorize(error);
      })
      .finally(() => {
        hideSpin();
      });
  };

  const handleTransferNumberChange = (value: string) => {
    setTransferNumber(value);
    setTransferNumberError('');

    // Normalize for comparison: treat country code only (like "1") as empty
    const normalizedValue = value && value.length > 1 ? value : '';
    const normalizedInitial =
      initialTransferNumber && initialTransferNumber.length > 1
        ? initialTransferNumber
        : '';

    setIsTransferNumberChanged(normalizedValue !== normalizedInitial);
  };

  const handleSaveTransferNumber = () => {
    // Clear any existing errors
    setTransferNumberError('');

    if (!decodedToken?.is_company_owner) {
      setTransferNumberError('You are not a company owner');
      return;
    }

    // Normalize the number (treat country code only as empty)
    const normalizedNumber =
      transferNumber && transferNumber.length > 1 ? transferNumber : '';

    // Validate phone number if not empty
    if (normalizedNumber && !isValidPhoneNumber(`+${normalizedNumber}`)) {
      setTransferNumberError('Invalid phone number');
      return;
    }

    showSpin();
    UpdateAiSettings(token, {
      ai_transfer_number: normalizedNumber ? `+${normalizedNumber}` : '',
      refresh: refreshToken,
    })
      .then((res) => {
        const responseData = res?.data?.data;
        if (responseData?.is_success) {
          message.success('Transfer number updated successfully');
          setInitialTransferNumber(normalizedNumber);
          setIsTransferNumberChanged(false);
          if (responseData?.data?.access) {
            localStorage.setItem('@token', responseData.data.access);
          }
          if (responseData?.data?.refresh) {
            localStorage.setItem('@refreshToken', responseData.data.refresh);
          }
        }
      })
      .catch((error) => {
        message.error('Failed to update transfer number');
        unauthorize(error);
      })
      .finally(() => {
        hideSpin();
      });
  };

  return (
    <div className='settings-content-wrapper'>
      <CustomTitle
        text={title}
        fontSize={20}
        fontWeight='700'
        lineHeight='32px'
        color='var(--text-primary)'
        marginBottom={12}
      />

      {toggle !== null && (
        <Flex
          align='center'
          gap={12}
          style={{
            background: 'var(--surface-container)',
          }}
        >
          <ToggleSwitch checked={toggle} onChange={toggleSwitch} />

          <div>
            <Text
              strong
              style={{
                fontSize: '12px',
                fontWeight: 600,
                color: 'var(--text-primary)',
              }}
            >
              Enable AI
            </Text>
            <Paragraph
              style={{
                margin: 0,
                fontSize: '12px',
                color: 'var(--text-light)',
              }}
            >
              Show options to use AI to resolve alerts
            </Paragraph>
          </div>
        </Flex>
      )}

      {toggle !== null && (
        <Flex
          vertical
          style={{
            background: 'var(--surface-container)',
            marginTop: 12,
          }}
        >
          <div>
            <Text
              strong
              style={{
                fontSize: '12px',
                fontWeight: 600,
                color: 'var(--text-primary)',
              }}
            >
              Phone number (inbound / outbound)
            </Text>
          </div>

          <div style={{ maxWidth: 300 }}>
            <style>
              {`
                .white-bg-phone-input-enabled .form-control {
                  background-color: #ffffff !important;
                  border: 2px solid #E8E8E8 !important;
                }
                .white-bg-phone-input-enabled .flag-dropdown {
                  background-color: #ffffff !important;
                  border: 2px solid #E8E8E8 !important;
                  border-right: none !important;
                }
                .white-bg-phone-input-enabled .selected-flag {
                  background-color: #ffffff !important;
                }
              `}
            </style>
            <div
              className={toggle ? 'white-bg-phone-input-enabled' : ''}
              style={{
                opacity: toggle ? 1 : 0.5,
                pointerEvents: 'none',
              }}
            >
              <PhoneInput
                country={'us'}
                value={agentNumber}
                disabled={!toggle}
                disableDropdown={true}
              />
            </div>
            <Paragraph
              style={{
                margin: '8px 0 0 0',
                fontSize: '12px',
                color: 'var(--text-light)',
              }}
            >
              This is the number used for outbound calls, and can also receive
              inbound calls
            </Paragraph>
          </div>

          <div style={{ marginTop: 12 }}>
            <Text
              strong
              style={{
                fontSize: '12px',
                fontWeight: 600,
                color: 'var(--text-primary)',
              }}
            >
              Escalation Phone Number (optional)
            </Text>
          </div>

          <div style={{ maxWidth: 300 }}>
            <div
              style={{
                opacity: toggle ? 1 : 0.5,
                pointerEvents: toggle ? 'auto' : 'none',
              }}
            >
              <PhoneInput
                country={'us'}
                value={transferNumber}
                onChange={handleTransferNumberChange}
                disabled={!toggle}
              />
            </div>
            {transferNumberError && (
              <div
                className='ant-form-item-explain-error'
                style={{ color: 'var(--danger)' }}
              >
                {transferNumberError}
              </div>
            )}
            <Paragraph
              style={{
                margin: '8px 0 0 0',
                fontSize: '12px',
                color: 'var(--text-light)',
              }}
            >
              In case user requests a real person during AI call
            </Paragraph>
            <Button
              type='primary'
              onClick={handleSaveTransferNumber}
              disabled={!toggle || !isTransferNumberChanged}
              style={{
                marginTop: 24,
                display: 'flex',
                height: '36px',
                width: '107px',
                padding: '10px 12px',
                alignItems: 'center',
                gap: '8px',
                borderRadius: '8px',
                background:
                  'var(--Inverse-Background-backgroundInversePrimary, #000)',
                color: '#fff',
              }}
            >
              Save
            </Button>
          </div>
        </Flex>
      )}
    </div>
  );
};

export default AIworkflow;
